import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import CardEditModal from './components/CardEditModal';
import { stripEmoji } from './utils/text';
import { suspendCard } from './utils/cards';

// Les 4 notes FSRS. L'ordre va du plus faible au plus fort rappel.
const RATINGS = [
  {
    key: 'oublie',
    label: 'Oublié',
    hint: 'Pas retrouvé',
    dot: 'bg-rating-forgot',
    hover: 'hover:border-rating-forgot/60 hover:bg-rating-forgot/[0.04]',
  },
  {
    key: 'difficile',
    label: 'Difficile',
    hint: 'Avec effort',
    dot: 'bg-rating-hard',
    hover: 'hover:border-rating-hard/60 hover:bg-rating-hard/[0.04]',
  },
  {
    key: 'moyen',
    label: 'Correct',
    hint: 'Normalement',
    dot: 'bg-rating-good',
    hover: 'hover:border-rating-good/60 hover:bg-rating-good/[0.04]',
  },
  {
    key: 'facile',
    label: 'Facile',
    hint: 'Immédiat',
    dot: 'bg-rating-easy',
    hover: 'hover:border-rating-easy/60 hover:bg-rating-easy/[0.04]',
  },
];

const FlashcardApp = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const deckId = searchParams.get('deck');

  const [cards, setCards] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isFlipped, setIsFlipped] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [deckName, setDeckName] = useState('');
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isSuspending, setIsSuspending] = useState(false);
  const [settings, setSettings] = useState({
    cardsPerSession: 10,
    autoFlipEnabled: false,
    autoFlipDelay: 5
  });

  // Charger les paramètres depuis localStorage
  useEffect(() => {
    const savedSettings = localStorage.getItem('flashcardSettings');
    if (savedSettings) {
      const parsed = JSON.parse(savedSettings);
      setSettings(parsed);
      console.log('⚙️ Paramètres chargés:', parsed);
    } else {
      console.log('⚙️ Aucun paramètre sauvegardé, utilisation des valeurs par défaut');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Fonction pour mélanger un tableau (Fisher-Yates shuffle)
  const shuffleArray = useCallback((array) => {
    const shuffled = [...array];
    for (let i = shuffled.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }
    return shuffled;
  }, []);

  const SESSION_KEY = `reviewSession_${deckId || 'all'}`;

  const loadCards = useCallback(async () => {
    // Restaurer la session en cours si elle existe pour ce deck
    try {
      const saved = sessionStorage.getItem(`reviewSession_${deckId || 'all'}`);
      if (saved) {
        const { savedCards, savedIndex } = JSON.parse(saved);
        if (savedCards?.length > 0 && savedIndex < savedCards.length) {
          setCards(savedCards);
          setCurrentIndex(savedIndex);
          if (savedCards[0]?.deck) setDeckName(savedCards[0].deck);
          setIsLoading(false);
          return; // Session restaurée, pas besoin de recharger
        }
      }
    } catch (_) {}

    try {
      // Construire l'URL avec le deckId si disponible
      const url = deckId
        ? `https://n8n.automationtekleabsam.com/webhook/load-cards?deckId=${deckId}`
        : 'https://n8n.automationtekleabsam.com/webhook/load-cards';

      console.log('🔍 URL appelée:', url);
      console.log('🎯 DeckId demandé:', deckId || 'TOUS LES DECKS');

      const response = await fetch(url);
      const data = await response.json();

      console.log('📊 Données reçues du webhook:');
      console.log('- success:', data.success);
      console.log('- count:', data.count);
      console.log('- nombre de cartes reçues:', data.cards?.length);
      console.log('- deckName:', data.deckName);
      console.log('- Première carte:', data.cards?.[0]);
      console.log('- Dernière carte:', data.cards?.[data.cards?.length - 1]);

      // Vérifier les decks des cartes reçues
      if (data.cards && data.cards.length > 0) {
        const decksInCards = [...new Set(data.cards.map(c => c.deck))];
        console.log('🏷️ Decks dans les cartes reçues:', decksInCards);

        // FILTRAGE SUPPLÉMENTAIRE côté client si nécessaire
        let filteredCards = data.cards;
        if (deckId && data.cards.some(c => c.deck)) {
          // Filtrer côté client aussi pour être sûr
          filteredCards = data.cards.filter(card => {
            if (!card.deck) return false;
            const cardDeckId = card.deck.toLowerCase().replace(/[^a-z0-9]/g, '-');
            const match = cardDeckId === deckId;
            if (!match) {
              console.log(`⚠️ Carte filtrée côté client: ${card.name} (deck: ${card.deck} ≠ ${deckId})`);
            }
            return match;
          });
          console.log(`✅ Filtrage client: ${filteredCards.length}/${data.cards.length} cartes gardées`);
        }

        // 🔀 Mélanger les cartes avant de les afficher
        const shuffledCards = shuffleArray(filteredCards);
        console.log('✅ Cartes mélangées:', shuffledCards.length);

        // 📏 Limiter le nombre de cartes selon les paramètres
        const savedSettings = localStorage.getItem('flashcardSettings');
        const cardsPerSession = savedSettings
          ? JSON.parse(savedSettings).cardsPerSession
          : 10;

        const limitedCards = shuffledCards.slice(0, cardsPerSession);
        console.log(`📊 Session limitée à ${limitedCards.length} cartes (paramètre: ${cardsPerSession})`);

        setCards(limitedCards);
        if (data.deckName) {
          setDeckName(data.deckName);
        }
      } else {
        console.error('❌ Pas de cartes dans la réponse ou success=false');
        setCards([]);
      }
      setIsLoading(false);
    } catch (error) {
      console.error('Erreur chargement cartes:', error);
      setIsLoading(false);
    }
  }, [deckId, shuffleArray]);

  // Charger les cartes depuis l'API
  useEffect(() => {
    loadCards();
  }, [loadCards]);

  // Sauvegarder la progression dans sessionStorage à chaque changement de carte
  useEffect(() => {
    if (cards.length > 0) {
      sessionStorage.setItem(SESSION_KEY, JSON.stringify({
        savedCards: cards,
        savedIndex: currentIndex
      }));
    }
  }, [currentIndex, cards, SESSION_KEY]);

  // Timer auto-flip - Ne fonctionne que si le modal n'est pas ouvert
  useEffect(() => {
    let timer;
    if (!isFlipped && settings.autoFlipEnabled && cards.length > 0 && !isEditModalOpen) {
      timer = setTimeout(() => {
        setIsFlipped(true);
      }, settings.autoFlipDelay * 1000);
    }
    return () => clearTimeout(timer);
  }, [currentIndex, isFlipped, settings.autoFlipEnabled, settings.autoFlipDelay, cards.length, isEditModalOpen]);

  // Barre d'espace = même effet qu'un clic sur la carte.
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.code !== 'Space' && e.key !== ' ') return;
      if (e.repeat || e.metaKey || e.ctrlKey || e.altKey) return;
      if (isEditModalOpen || cards.length === 0) return;

      const target = e.target;
      const tag = target?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || target?.isContentEditable) {
        return;
      }

      // La carte est un <button> : si elle a déjà le focus (après un premier
      // clic), le navigateur va déclencher son onClick tout seul. Le laisser
      // faire évite de basculer deux fois.
      if (target?.closest?.('[data-flip-card]')) return;

      // Ailleurs sur la page : on retourne nous-mêmes, et preventDefault
      // empêche l'espace de faire défiler la page.
      e.preventDefault();
      setIsFlipped((flipped) => !flipped);
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isEditModalOpen, cards.length]);

  const handleAnswer = async (difficulty) => {
    const currentCard = cards[currentIndex];

    try {
      const response = await fetch('https://n8n.automationtekleabsam.com/webhook/update-card', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          pageId: currentCard.pageId,
          difficulty: difficulty
        })
      });

      const result = await response.json();
      console.log('✅ Réponse update-card:', result);

      // Passer à la carte suivante
      if (currentIndex < cards.length - 1) {
        setCurrentIndex(currentIndex + 1);
        setIsFlipped(false);
      } else {
        // Session terminée — vider la session sauvegardée
        sessionStorage.removeItem(SESSION_KEY);
        alert('Session terminée ! 🎉');
        navigate('/');
      }
    } catch (error) {
      console.error('Erreur mise à jour carte:', error);
    }
  };

  const handleSuspendCard = async () => {
    if (isSuspending) return;

    const currentCard = cards[currentIndex];

    if (!window.confirm(
      'Suspendre cette carte ?\n\n' +
      'Elle sort de la révision et reste consultable dans la bibliothèque, ' +
      "sous le filtre « Suspendues », où vous pourrez la corriger puis la remettre en circulation."
    )) {
      return;
    }

    setIsSuspending(true);
    try {
      const result = await suspendCard(currentCard.pageId);
      console.log('✅ Carte suspendue:', result);

      // Retirer la carte de la session et passer à la suivante
      const updatedCards = cards.filter((_, index) => index !== currentIndex);
      setCards(updatedCards);

      // La carte suspendue était la dernière de la session : il ne reste rien
      // à noter, autant terminer plutôt que de renvoyer sur une carte déjà vue.
      if (currentIndex >= updatedCards.length) {
        sessionStorage.removeItem(SESSION_KEY);
        alert('Carte suspendue. Session terminée ! 🎉');
        navigate('/');
      } else {
        // Rester au même index : il affiche maintenant la carte suivante
        setIsFlipped(false);
      }
    } catch (error) {
      console.error('Erreur suspension carte:', error);
      alert('Erreur lors de la suspension de la carte.');
    } finally {
      setIsSuspending(false);
    }
  };

  // Ouvrir le modal d'édition
  const handleEditCard = () => {
    setIsEditModalOpen(true);
  };

  // Sauvegarder les modifications de la carte
  const handleSaveCard = (updatedCard) => {
    // Mettre à jour la carte dans l'état local
    const updatedCards = [...cards];
    updatedCards[currentIndex] = { ...updatedCards[currentIndex], ...updatedCard };
    setCards(updatedCards);
    setIsEditModalOpen(false);
  };

  if (isLoading) {
    return (
      <div className="min-h-screen pb-24 flex items-center justify-center">
        <div className="text-center">
          <div className="w-8 h-8 mx-auto mb-4 rounded-full border-2 border-sand-300 border-t-clay-500 animate-spin" />
          <div className="label-caps">Chargement des cartes</div>
        </div>
      </div>
    );
  }

  if (cards.length === 0) {
    return (
      <div className="min-h-screen pb-24 flex items-center justify-center px-6">
        <div className="text-center max-w-sm">
          <h2 className="font-display text-3xl text-ink-900 mb-3">
            Rien à réviser
          </h2>
          <p className="text-ink-500 leading-relaxed mb-8">
            Toutes vos cartes sont à jour. Revenez quand la prochaine
            échéance sera atteinte.
          </p>
          <button
            onClick={() => navigate('/')}
            className="px-6 py-3 border border-sand-300 rounded-xl text-ink-900 font-medium hover:bg-sand-100 transition-colors"
          >
            Retour à l'accueil
          </button>
        </div>
      </div>
    );
  }

  const currentCard = cards[currentIndex];
  const progress = ((currentIndex) / cards.length) * 100;

  return (
    <div className="min-h-screen pb-28">
      {/* En-tête */}
      <header className="bg-sand-50/90 backdrop-blur border-b border-sand-200 sticky top-0 z-40">
        <div className="max-w-2xl mx-auto px-5 pt-4 pb-3">
          <div className="flex items-center justify-between gap-4">
            <button
              onClick={() => navigate('/')}
              className="-ml-1 p-1.5 text-ink-500 hover:text-ink-900 transition-colors"
              aria-label="Retour"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth={1.75} viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
              </svg>
            </button>

            <div className="min-w-0 flex-1 text-center">
              <div className="label-caps truncate">
                {deckName || 'Toutes les cartes'}
              </div>
            </div>

            <div className="flex items-center gap-1">
              {settings.autoFlipEnabled && !isFlipped && (
                <span className="label-caps text-clay-600 mr-1">
                  {settings.autoFlipDelay}s
                </span>
              )}
              <button
                onClick={handleEditCard}
                className="p-1.5 text-ink-500 hover:text-clay-600 transition-colors"
                aria-label="Éditer cette carte"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth={1.75} viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                </svg>
              </button>

              {/* Suspendre reste atteignable dès la question : on sait souvent
                  qu'une carte est à retirer avant même de l'avoir retournée. */}
              <button
                onClick={handleSuspendCard}
                disabled={isSuspending}
                className="p-1.5 text-ink-500 hover:text-rating-hard disabled:text-sand-300 transition-colors"
                aria-label="Suspendre cette carte"
                title="Suspendre cette carte"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth={1.75} viewBox="0 0 24 24">
                  <circle cx="12" cy="12" r="9" />
                  <path strokeLinecap="round" d="M10 9.5v5M14 9.5v5" />
                </svg>
              </button>
            </div>
          </div>

          {/* Progression de la session */}
          <div className="mt-3 flex items-center gap-3">
            <div className="flex-1 h-[3px] bg-sand-200 rounded-full overflow-hidden">
              <div
                className="h-full bg-clay-500 rounded-full transition-all duration-500 ease-out"
                style={{ width: `${progress}%` }}
              />
            </div>
            <span className="label-caps tabular-nums">
              {currentIndex + 1}&thinsp;/&thinsp;{cards.length}
            </span>
          </div>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-5">
        {/* Carte */}
        <button
          type="button"
          data-flip-card
          onClick={() => setIsFlipped(!isFlipped)}
          className="paper w-full text-left border rounded-xl shadow-lift
                     px-7 py-12 sm:py-16 mt-6 min-h-[19rem] flex flex-col justify-center
                     transition-colors"
        >
          {!isFlipped ? (
            <div className="text-center">
              <div className="label-caps mb-5">Question</div>
              <div className="font-display text-[2rem] sm:text-[2.5rem] leading-tight">
                {currentCard.name}
              </div>
              {currentCard.frenchText && (
                <div className="mt-3 paper-muted text-lg">
                  {currentCard.frenchText}
                </div>
              )}
              <div className="label-caps mt-10 opacity-60">
                Toucher<span className="hidden sm:inline"> ou barre d'espace</span> pour révéler
              </div>
            </div>
          ) : (
            <div className="text-center">
              <div className="label-caps mb-5">Tigrigna</div>
              <div className="font-geez text-[2.5rem] sm:text-[3.25rem] font-medium">
                {currentCard.translationTigrinya}
              </div>
              {currentCard.pronunciation && (
                <div className="label-caps mt-4 paper-accent">
                  {currentCard.pronunciation}
                </div>
              )}
              {currentCard.context && (
                <div className="mt-8 pt-6 border-t paper-rule max-w-md mx-auto">
                  <div className="label-caps mb-2">Exemple</div>
                  <p className="font-display italic paper-muted leading-relaxed">
                    {currentCard.context}
                  </p>
                </div>
              )}
            </div>
          )}
        </button>

        {/* Notation */}
        {isFlipped && (
          <div className="mt-5">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5">
              {RATINGS.map((rating) => (
                <button
                  key={rating.key}
                  onClick={() => handleAnswer(rating.key)}
                  className={`px-3 py-4 bg-surface border border-sand-200 rounded-xl
                              text-left transition-colors ${rating.hover}`}
                >
                  <span className="flex items-center gap-2">
                    <span className={`w-1.5 h-1.5 rounded-full ${rating.dot}`} />
                    <span className="font-medium text-ink-900 text-[15px]">
                      {rating.label}
                    </span>
                  </span>
                  <span className="block mt-1 text-[12px] text-ink-400 pl-[14px]">
                    {rating.hint}
                  </span>
                </button>
              ))}
            </div>

            <button
              onClick={handleSuspendCard}
              disabled={isSuspending}
              className="w-full mt-3 py-2.5 border border-sand-200 rounded-xl label-caps
                         hover:border-rating-hard hover:text-rating-hard
                         disabled:text-sand-300 disabled:border-sand-200 transition-colors"
            >
              {isSuspending ? 'Suspension…' : 'Suspendre — retirer de la révision'}
            </button>
          </div>
        )}

        {/* Historique de la carte */}
        <div className="mt-8 flex items-stretch divide-x divide-sand-200 border-t border-sand-200 pt-5">
          <div className="flex-1 text-center px-2">
            <div className="text-ink-900 font-medium truncate">{stripEmoji(currentCard.level)}</div>
            <div className="label-caps mt-0.5">Niveau</div>
          </div>
          <div className="flex-1 text-center px-2">
            <div className="text-ink-900 font-medium tabular-nums">{currentCard.reviewCount}</div>
            <div className="label-caps mt-0.5">Révisions</div>
          </div>
          <div className="flex-1 text-center px-2">
            <div className="text-ink-900 font-medium tabular-nums">{currentCard.successRate}%</div>
            <div className="label-caps mt-0.5">Réussite</div>
          </div>
        </div>
      </main>

      {/* Modal d'édition */}
      <CardEditModal
        card={currentCard}
        isOpen={isEditModalOpen}
        onClose={() => setIsEditModalOpen(false)}
        onSave={handleSaveCard}
      />
    </div>
  );
};

export default FlashcardApp;
